import React, { useState, useEffect } from 'react';
import type { QuickCalcScreen as QuickCalcScreenType } from '../../types/course';
import { playCorrectSound, playIncorrectSound } from '../../utils/sound';

interface Props {
  screen: QuickCalcScreenType;
  onInteractionComplete: () => void;
  completed: boolean;
}

export const QuickCalcScreen: React.FC<Props> = ({
  screen,
  onInteractionComplete,
  completed,
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(
    completed ? screen.correctIndex : null
  );
  const [showExplanation, setShowExplanation] = useState(completed);

  useEffect(() => {
    if (selectedIndex !== null && !completed) {
      onInteractionComplete();
    }
  }, [selectedIndex, completed, onInteractionComplete]);

  const handleSelect = (index: number) => {
    if (selectedIndex !== null) return;
    setSelectedIndex(index);
    setShowExplanation(true);
    if (index === screen.correctIndex) {
      playCorrectSound();
    } else {
      playIncorrectSound();
    }
  };

  return (
    <div className="flex flex-col gap-4 px-1">
      {screen.icon && <div className="text-3xl text-center">{screen.icon}</div>}
      <h3 className="text-lg font-semibold font-serif text-[var(--clue-text-primary)] text-center">
        {screen.title}
      </h3>

      {screen.body && (
        <>
          <p className="text-sm leading-relaxed text-[var(--clue-text-secondary)]">
            {screen.body}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-px bg-[var(--clue-border-mid)]" />
            <span className="text-[10px] font-semibold text-[var(--clue-text-tertiary)] uppercase tracking-widest">Your turn</span>
            <div className="flex-1 h-px bg-[var(--clue-border-mid)]" />
          </div>
        </>
      )}

      <p className="text-sm leading-relaxed text-[var(--clue-text-secondary)]">
        {screen.scenario}
      </p>

      {screen.formula && (
        <div className="rounded-lg bg-[var(--clue-bg-secondary)] border border-[var(--clue-border-mid)] px-4 py-2.5 text-center">
          <span className="text-xs text-[var(--clue-text-tertiary)] block mb-0.5">Formula</span>
          <span className="text-sm font-mono text-[var(--clue-text-primary)]">{screen.formula}</span>
        </div>
      )}

      <div className="space-y-2">
        {screen.options.map((option, i) => {
          let optionStyle =
            'border-[var(--clue-border-mid)] bg-[var(--clue-bg-primary)] hover:bg-[var(--clue-bg-secondary)]';

          if (selectedIndex !== null) {
            if (i === screen.correctIndex) {
              optionStyle =
                'border-[var(--clue-green-accent)] bg-[var(--clue-green-accent)]/10 ring-1 ring-[var(--clue-green-accent)]';
            } else if (i === selectedIndex) {
              optionStyle = 'border-red-400 bg-red-500/10 ring-1 ring-red-400';
            } else {
              optionStyle = 'border-[var(--clue-border-mid)] bg-[var(--clue-bg-primary)] opacity-50';
            }
          }

          return (
            <button
              key={i}
              onClick={() => handleSelect(i)}
              disabled={selectedIndex !== null}
              className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-all duration-200 ${optionStyle}`}
            >
              <span className="text-[var(--clue-text-primary)]">{option}</span>
            </button>
          );
        })}
      </div>

      {showExplanation && (
        <div
          className={`rounded-lg p-4 border ${
            selectedIndex === screen.correctIndex
              ? 'bg-[var(--clue-green-accent)]/10 border-[var(--clue-green-accent)]/30'
              : 'bg-red-500/10 border-red-400/30'
          }`}
        >
          <p className="text-xs font-semibold mb-1 text-[var(--clue-text-primary)]">
            {selectedIndex === screen.correctIndex ? 'Correct!' : 'Not quite!'}
          </p>
          <p className="text-sm text-[var(--clue-text-secondary)] leading-relaxed">
            {screen.explanation}
          </p>
        </div>
      )}
    </div>
  );
};
